import type { Tenant } from "./TenantProvider";

/**
 * Extract a display name from a tenant's scopes
 */
export function getTenantDisplayName(tenant: Tenant): string {
  const names = new Set<string>();
  for (const scope of tenant.scopes) {
    if (scope.kind === "customer" || scope.kind === "branch") {
      if (scope.name) names.add(scope.name);
    } else if (scope.kind === "test") {
      names.add(scope.unique);
    }
  }

  if (names.size === 0) {
    return tenant.id;
  }

  return Array.from(names).join(", ");
}

/**
 * Strips the `/tenants/<id>` prefix from a resolved pathname and returns the
 * remainder (always leading-slashed, e.g. `/reports/wastesense`). Returns
 * `null` when the path isn't tenant-scoped. The tenant id is matched as a
 * whole segment, so `/tenants/T123` → `/` and `/tenants/T123/reports` →
 * `/reports`.
 */
export function getTenantSubpath(pathname: string): string | null {
  const match = pathname.match(/^\/tenants\/[^/]+(\/.*)?$/);
  if (!match) return null;
  return match[1] ?? "/";
}

/**
 * The tenant a tenant-scoped path belongs to — `/tenants/<id>/...` → `<id>` —
 * or `null` for a path outside any tenant.
 */
export function getPathTenantId(path: string): string | null {
  return path.match(/^\/tenants\/([^/?#]+)/)?.[1] ?? null;
}

/**
 * Builds a tenant-scoped path (`/tenants/<id>/<subpath>`) — the inverse of
 * {@link getTenantSubpath}. Centralising the prefix means call sites stop
 * hand-writing `/tenants/${id}/...` template strings (easy to typo, and the
 * thing that has to change if the tenant route shape ever moves).
 *
 * `subpath` may be given with or without a leading slash; an empty subpath
 * (or `"/"`) yields the bare tenant root. Query strings are preserved.
 *
 * Returns `undefined` when `tenantId` is missing so callers can feed it
 * straight into an optional `href` (an absent tenant → a non-link crumb)
 * rather than emitting a tenant-less `/tenants//...` URL.
 */
export function tenantPath(tenantId: string | null | undefined, subpath = ""): string | undefined {
  if (!tenantId) return undefined;
  const trimmed = subpath.replace(/^\//, "");
  return trimmed ? `/tenants/${tenantId}/${trimmed}` : `/tenants/${tenantId}`;
}

/**
 * True when `pathname` is at — or under — the given subpath of the current
 * tenant, i.e. `/tenants/<id>/<subpath>` or any of its children. Pairs with
 * {@link getTenantSubpath} (whose output it's matched against) and matches
 * `subpath` as whole path segments, so it won't be fooled by substrings
 * (`reports` does not match a hypothetical `/reports-export`, and an
 * unrelated `/foo/reports` is rejected because it isn't tenant-scoped).
 */
export function isTenantSubpath(pathname: string, subpath: string): boolean {
  const current = getTenantSubpath(pathname);
  if (current === null) return false;
  const target = subpath.replace(/^\//, "");
  return current === `/${target}` || current.startsWith(`/${target}/`);
}

/**
 * Filter tenants by display name or ID against a search query.
 * Returns all tenants when the query is empty or whitespace-only.
 * Results are sorted alphabetically by display name using locale-aware comparison.
 */
export function filterTenants(tenants: readonly Tenant[], searchQuery: string): readonly Tenant[] {
  const collator = new Intl.Collator("en-AU", { sensitivity: "base" });
  const sorted = [...tenants].sort((a, b) => collator.compare(getTenantDisplayName(a), getTenantDisplayName(b)));
  if (!searchQuery.trim()) return sorted;
  const query = searchQuery.toLowerCase();
  return sorted.filter(
    (tenant) => getTenantDisplayName(tenant).toLowerCase().includes(query) || tenant.id.toLowerCase().includes(query),
  );
}
